import type { CurrentUser, TokenPairResponse } from './auth';
import type { components } from './generated/schema';
import { resolveApiUrl } from './client';

export class AccountDeletionError extends Error {
  constructor(readonly reason: 'credentials' | 'patient' | 'request') {
    super(reason);
  }
}

// Patient credentials are deliberately isolated from the staff auth provider.
// In particular, never retry a patient request using the CRM refresh token.
async function request<T>(path: string, body?: unknown, token?: string): Promise<T> {
  const response = await fetch(resolveApiUrl(path), {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': 'uk',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    throw new AccountDeletionError(response.status === 401 ? 'credentials' : 'request');
  }
  return response.status === 204 ? undefined as T : response.json() as Promise<T>;
}

export async function requestPatientAccountDeletion(email: string, password: string): Promise<string> {
  const tokens = await request<TokenPairResponse>('/auth/login', { email: email.trim(), password });
  try {
    const user = await request<CurrentUser>('/auth/me', undefined, tokens.access_token);
    if (!user.has_patient_profile || user.has_admin_profile || user.roles.some(role => role.toLowerCase() !== 'patient')) {
      throw new AccountDeletionError('patient');
    }
    const body: components['schemas']['DeleteAccountRequest'] = { password, confirm: true };
    const result = await request<components['schemas']['AccountDeletionScheduledResponse']>(
      '/auth/delete-account', body, tokens.access_token,
    );
    return result.scheduled_deletion_at;
  } finally {
    // A logout failure must not hide a successfully accepted deletion request.
    await request<void>('/auth/logout', { refresh_token: tokens.refresh_token }).catch(() => undefined);
  }
}
